import { action, computed, makeObservable, observable } from 'mobx';
import { defaultSelectedFranchiseProfile, franchiseToastErrorId } from '../constants/franchiseManagement';
import { IFranchiseErrorMessages, TFranchise, TFranchiseBranch, franchiseCast } from '../types/franchiseManagement';
import { PER_PAGE } from '~/constants/main';
import client from '~/services/client';
import logger from '~/services/logger';
import { LoadingStatus } from '~/types/main';
import { arrayMerge } from '~/utils/formatter';
import { removeToast } from '~/utils/toasts';

class FranchiseManagementStore {
  constructor() {
    makeObservable(this);
  }

  @observable franchisesList: TFranchise[] = [];
  @observable selectedFranchiseProfile: TFranchise = defaultSelectedFranchiseProfile;
  @observable errorMessages: IFranchiseErrorMessages = { vat: null, vatRegNumber: null, certificate: null };
  @observable isDataEdited: boolean = false;
  @observable loadingStatus: LoadingStatus = LoadingStatus.Init;

  @computed get isSelectedDefaultProfile(): boolean {
    return Boolean(this.selectedFranchiseProfile.is_default);
  }

  @action setSelectedProfile = (id: number) => {
    const profile = this.franchisesList.find((franchise) => franchise.id === id);
    this.selectedFranchiseProfile = profile || defaultSelectedFranchiseProfile;
    this.errorMessages = { vat: null, vatRegNumber: null, certificate: null };
    this.isDataEdited = false;
  };

  @action setProfileBranches = (branches: TFranchiseBranch[]) => {
    this.selectedFranchiseProfile.branches = branches;
  };

  @action removeBranchFromProfile = (branchId: number) => {
    this.selectedFranchiseProfile = {
      ...this.selectedFranchiseProfile,
      branches: this.selectedFranchiseProfile.branches.filter((branch) => branch.id !== branchId),
    };
    this.isDataEdited = true;
  };

  @action changeFranchiseProfileSettings = (data: Partial<TFranchise>) => {
    this.selectedFranchiseProfile = { ...this.selectedFranchiseProfile, ...data };
    this.franchisesList = this.franchisesList.map((profile) =>
      profile.id === this.selectedFranchiseProfile.id ? this.selectedFranchiseProfile : profile
    );
    this.isDataEdited = true;
  };

  @action fetchFranchisesList = async () => {
    try {
      this.loadingStatus = LoadingStatus.Loading;

      this.franchisesList = await this._getFranchisesList();
      this.setSelectedProfile(this.franchisesList[0]?.id);

      this.selectedFranchiseProfile = this.franchisesList[0];
      this.loadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'FranchiseManagementStore fetchFranchisesList' });
    }
  };

  @action private _getFranchisesList = async () => {
    try {
      const list = await client.get('v3/franchises', { params: { per_page: PER_PAGE.MAXIMUM } });
      return list.data.map(franchiseCast);
    } catch (error: any) {
      logger.error({ error, place: 'FranchiseManagementStore _getFranchisesList' });
    }
  };

  @action createProfile = async () => {
    try {
      this.loadingStatus = LoadingStatus.Refreshing;

      const createdProfile = await client.post('v3/franchises', {});
      const transformedProfile: TFranchise = franchiseCast(createdProfile);

      this.franchisesList.push({ ...defaultSelectedFranchiseProfile, ...transformedProfile });
      this.setSelectedProfile(transformedProfile.id);

      this.loadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'FranchiseManagementStore createProfile' });
    }
  };

  @action updateProfileData = async () => {
    try {
      this.loadingStatus = LoadingStatus.Refreshing;

      const updatedProfile = await client.post(`v3/franchises/store`, this._formedApiData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const transformedProfile: TFranchise = franchiseCast(updatedProfile);

      // update list silently
      const list = await this._getFranchisesList();
      this.franchisesList = arrayMerge(list, [transformedProfile], 'id');
      this.selectedFranchiseProfile = transformedProfile;

      this.isDataEdited = false;
      this.loadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'FranchiseManagementStore updateProfileData' });
    }
  };

  @action deleteProfile = async (id: number) => {
    try {
      this.loadingStatus = LoadingStatus.Refreshing;

      await client.delete(`v3/franchises/${id}`);

      this.fetchFranchisesList();
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'FranchiseManagementStore deleteProfile' });
    }
  };

  @action validateVat = () => {
    this.errorMessages.vat = !this.selectedFranchiseProfile.vat ? 'required_field_error' : null;
  };

  @action validateVatRegNumber = () => {
    this.errorMessages.vatRegNumber = !this.selectedFranchiseProfile.vatRegistrationNumber ? 'required_field_error' : null;
  };

  @action validateCertificateFile = () => {
    const isValidCertificateFile = this.selectedFranchiseProfile.certificate.type === 'application/pdf';

    if (!isValidCertificateFile) {
      this.errorMessages.certificate = 'Please upload PDF file';
      this.selectedFranchiseProfile.certificate = new File([], '');
      this.selectedFranchiseProfile.certificateFilename = '';
    } else {
      this.errorMessages.certificate = null;
    }
  };

  validateBeforeSave = () => {
    this.validateVat();
    this.validateVatRegNumber();
  };

  @computed get isValidationFailed() {
    return Object.values(this.errorMessages).some((msg) => msg !== null);
  }

  @action reset = () => {
    this.franchisesList = [];
    this.selectedFranchiseProfile = defaultSelectedFranchiseProfile;
    this.errorMessages = { vat: null, vatRegNumber: null, certificate: null };
    this.isDataEdited = false;
    this.loadingStatus = LoadingStatus.Init;

    removeToast(franchiseToastErrorId);
  };

  private get _formedApiData() {
    const formData = new FormData();

    if (this.selectedFranchiseProfile.certificate.size) {
      formData.append('certificate', this.selectedFranchiseProfile.certificate);
    }

    if (!this.selectedFranchiseProfile.certificateFilename && this.selectedFranchiseProfile.certificateUrl) {
      formData.append('certificate_delete', '1');
    }

    const data = {
      id: this.selectedFranchiseProfile.id,
      title: this.selectedFranchiseProfile.name,
      vat: this.selectedFranchiseProfile.vat,
      vat_included: this.selectedFranchiseProfile.vatIncluded,
      taxable_delivery_fee: this.selectedFranchiseProfile.taxableDeliveryFee,
      vat_registration_number: this.selectedFranchiseProfile.vatRegistrationNumber,
      franchise_currency: this.selectedFranchiseProfile.currencyId,
      franchise_timezone: this.selectedFranchiseProfile.timezone,
    };

    this.selectedFranchiseProfile.branches.forEach((branch, index) => {
      formData.append(`branches[${index}]`, String(branch.id));
    });

    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value.toString());
    });

    return formData;
  }
}

export const franchiseManagementStore = new FranchiseManagementStore();
